'use client';

import type { FC, FormEvent } from 'react';
import { Button } from 'src/components/Button';
import { InputText } from 'src/components/InputText';
import type { UseFormReturn } from 'src/hooks';
import { X, Save, Package, DollarSign } from 'lucide-react';
import type { GradeSize } from 'src/model/GradeSize';

interface EditGradeSizeProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  submitGradesize: (e: FormEvent) => void;
  onClose: () => void;
  currentSize?: GradeSize;
}

export enum EditGradeSizes {
  size = 'size',
  stock = 'stock',
  price = 'price',
  discount = 'discount',
}

export const EditGradeSize: FC<EditGradeSizeProps> = ({
  formData,
  formErrors,
  onChangeFormInput,
  submitGradesize,
  onClose,
  currentSize,
}) => {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitGradesize(e);
  };

  const calculateFinalPrice = () => {
    const price = Number(formData[EditGradeSizes.price]) || 0;
    const discount = Number(formData[EditGradeSizes.discount]) || 0;
    return price * (1 - discount / 100);
  };

  return (
    <div className="w-full">
      <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-1">
              Editar Tamanho
            </h2>
            <p className="text-red-100 text-sm">
              Atualize as informações do tamanho selecionado
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors ml-4"
            aria-label="Fechar"
            type="button"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {currentSize && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
            <p className="text-sm font-semibold text-red-900 mb-3">
              Informações atuais:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-red-700 block mb-1">Tamanho:</span>
                <p className="font-bold text-red-900">{currentSize.size}</p>
              </div>
              <div>
                <span className="text-red-700 block mb-1">Estoque:</span>
                <p className="font-bold text-red-900">{currentSize.stock}</p>
              </div>
              <div>
                <span className="text-red-700 block mb-1">Preço:</span>
                <p className="font-bold text-red-900">
                  R$ {Number(currentSize.price.price).toFixed(2)}
                </p>
              </div>
              <div>
                <span className="text-red-700 block mb-1">Desconto:</span>
                <p className="font-bold text-red-900">
                  {currentSize.price.discount}%
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
              <Package className="w-5 h-5 text-red-600" />
              <h3 className="text-base font-bold text-gray-900">
                Informações Básicas
              </h3>
            </div>

            <InputText
              label="Tamanho"
              name="size"
              type="text"
              id="size"
              value={formData[EditGradeSizes.size]}
              onChange={(e) =>
                onChangeFormInput(EditGradeSizes.size)(e.target.value)
              }
              error={formErrors.size?.[0]}
              inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              placeholder="Ex: P, M, G, 38, 40"
            />

            <InputText
              label="Quantidade em Estoque"
              name="stock"
              type="number"
              id="stock"
              value={formData[EditGradeSizes.stock]}
              onChange={(e) =>
                onChangeFormInput(EditGradeSizes.stock)(e.target.value)
              }
              error={formErrors.stock?.[0]}
              inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              placeholder="Quantidade disponível"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
              <DollarSign className="w-5 h-5 text-red-600" />
              <h3 className="text-base font-bold text-gray-900">
                Precificação
              </h3>
            </div>

            <InputText
              label="Preço Regular (R$)"
              name="price"
              type="number"
              id="price"
              step="0.01"
              value={formData[EditGradeSizes.price]}
              onChange={(e) =>
                onChangeFormInput(EditGradeSizes.price)(e.target.value)
              }
              error={formErrors.price?.[0]}
              inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              placeholder="99.90"
            />

            <InputText
              label="Desconto (%)"
              name="discount"
              type="number"
              id="discount"
              step="0.01"
              value={formData[EditGradeSizes.discount]}
              onChange={(e) =>
                onChangeFormInput(EditGradeSizes.discount)(e.target.value)
              }
              error={formErrors.discount?.[0]}
              inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              placeholder="10"
            />

            {formData[EditGradeSizes.price] && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-red-900 mb-2">
                  Preço Final:
                </p>
                <div className="flex items-baseline gap-2">
                  {formData[EditGradeSizes.discount] &&
                  Number(formData[EditGradeSizes.discount]) > 0 ? (
                    <>
                      <span className="text-base text-gray-500 line-through">
                        R$ {Number(formData[EditGradeSizes.price]).toFixed(2)}
                      </span>
                      <span className="text-2xl font-bold text-red-700">
                        R$ {calculateFinalPrice().toFixed(2)}
                      </span>
                      <span className="text-xs bg-red-600 text-white px-2 py-1 rounded-full font-bold">
                        -{formData[EditGradeSizes.discount]}%
                      </span>
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-gray-900">
                      R$ {Number(formData[EditGradeSizes.price]).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <Button
            type="submit"
            buttonStyle="primary"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
            title="Salvar Alterações"
          >
            Salvar Alterações
            <Save className="w-5 h-5" />
          </Button>
          <Button
            type="button"
            onClick={onClose}
            buttonStyle="red"
            className="flex-1 sm:flex-none bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            title="Cancelar"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </form>
    </div>
  );
};
